import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';

export const Hero = ({ onQuoteClick }) => {
  const features = [
    'Licensed & Insured',
    'Free Estimates',
    '24/7 Emergency Service',
  ];

  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative min-h-screen flex items-center bg-slate-50 overflow-hidden"
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23334155' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }} />
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-0 items-center min-h-screen pt-24 lg:pt-0">
          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="z-10"
          >
            <span className="inline-block font-heading text-sm font-semibold tracking-widest uppercase text-orange-500 mb-4">
              Florida's Trusted Experts
            </span>
            
            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tighter uppercase text-slate-900 leading-none mb-6">
              Precision
              <span className="block text-orange-500">Concrete</span>
              <span className="block">Cutting</span>
            </h1>

            <p className="text-lg text-slate-600 max-w-xl mb-8 leading-relaxed">
              Professional concrete cutting, core drilling, and demolition services 
              throughout Florida. We deliver precision results on every project, 
              big or small.
            </p>

            {/* Features */}
            <div className="flex flex-wrap gap-4 mb-10">
              {features.map((feature) => (
                <div
                  key={feature}
                  className="flex items-center gap-2 text-slate-700"
                >
                  <CheckCircle2 className="h-5 w-5 text-orange-500" />
                  <span className="font-medium">{feature}</span>
                </div>
              ))}
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                onClick={onQuoteClick}
                data-testid="hero-quote-btn"
                className="bg-orange-500 hover:bg-orange-600 text-white rounded-none px-8 py-6 font-bold uppercase tracking-wider text-base transition-all hover:shadow-[4px_4px_0px_0px_rgba(51,65,85,1)]"
              >
                Get Free Quote
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button
                variant="outline"
                onClick={() => document.querySelector('#services').scrollIntoView({ behavior: 'smooth' })}
                data-testid="hero-services-btn"
                className="bg-white text-slate-900 border-2 border-slate-900 hover:bg-slate-50 rounded-none px-8 py-6 font-bold uppercase tracking-wider text-base"
              >
                Our Services
              </Button>
            </div>
          </motion.div>

          {/* Hero Image */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative lg:absolute lg:right-0 lg:top-0 lg:bottom-0 lg:w-1/2"
          >
            <div
              className="relative h-[400px] lg:h-full w-full"
              style={{
                clipPath: 'polygon(10% 0, 100% 0, 100% 100%, 0% 100%)',
              }}
            >
              <img
                src="https://images.unsplash.com/photo-1567238563567-b99d8ac66e9b?auto=format&fit=crop&w=1200&q=80"
                alt="Construction worker cutting concrete"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-slate-900/30 to-transparent" />
              
              {/* Orange accent bar */}
              <div className="absolute left-0 top-1/4 bottom-1/4 w-2 bg-orange-500" />
            </div>
          </motion.div>
        </div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2 hidden lg:block"
      >
        <div className="flex flex-col items-center gap-2 text-slate-400">
          <span className="text-xs font-medium tracking-widest uppercase">Scroll</span>
          <div className="w-px h-12 bg-gradient-to-b from-slate-400 to-transparent" />
        </div>
      </motion.div>
    </section>
  );
};

export default Hero;
